import _ from "lodash";

import {
  getPermissionBasicDetailsFromCache,
  getFeatureFlagBasicDetailsFromCache,
  getPreferenceBasicDetailsFromCache,
  getConfigurationBasicDetailsFromCache,
} from "./modules/GraphqlHelpers";

import {
  parsePreferenceValueObject,
  getContextIdForRolesAndPermissions,
  getContextIdForConfigurations,
} from "./modules/Utils";

const PERMISSION = "PERMISSION";
const FEATURE_FLAG = "FEATURE_FLAG";
const PREFERENCE = "PREFERENCE";
const MODULE_SETTINGS = "MODULE_SETTINGS";

import {
  ADD_ON_MODULE_SETTINGS_MAP,
  ADD_ON_PERMISSIONS_MAP,
} from "@toddle-edu/common-utils";
import ACLStore from "lib/aclStore";

const _ADD_ON_CONTROL_PARAMETERS_MAP_OBJECT = [
  ...ADD_ON_MODULE_SETTINGS_MAP,
  ...ADD_ON_PERMISSIONS_MAP,
].reduce((acc, { feature_flag, controlParameter }) => {
  return { ...acc, [controlParameter]: feature_flag };
}, {});

class ACLStorage {
  #contextIds = [];
  /**
   * Q: Why do we need this array?
   * A: Data is cached as per context array only. It does not consider other parameters like MODULE.
   *    This is intended because MODULE acts as a filter rather than a context.
   *
   *    Now, since component is fetching data wrt to context and module, we can't get rid of context when
   *    component is unmounted as other components might be using the same context.
   *
   *    Therefore, we are storing count of component relying on the particular context. When count becomes 0,
   *    we can remove the context.
   *
   * Q: Can't we achive the same without tracking count?
   * A: It is difficult to achieve as of now, as component mount/execute and unmount events affect
   *    the context array and we can't simply remove the context and add it back when component is mounted.
   */
  #countOfComponentsUsingParticularContext = [];

  //TEMPORARRY: This will be removed after discussion with the tech team.
  #contextsAndContextIdMap = {};

  #getConfigurationsValueForStaffByContext(configuration, contexts) {
    const contextId = getContextIdForConfigurations(contexts);

    return (
      parsePreferenceValueObject(
        getConfigurationBasicDetailsFromCache(`${contextId}::${configuration}`)
      ) === true
    );
  }

  #getControlParameterValueObjectByContextId(
    controlParameterName,
    controlParameterType,
    contextId
  ) {
    switch (controlParameterType) {
      case MODULE_SETTINGS:
      case PERMISSION:
        return getPermissionBasicDetailsFromCache(
          `${contextId}::${controlParameterName}`
        );

      case FEATURE_FLAG:
        return getFeatureFlagBasicDetailsFromCache(
          `${contextId}::${controlParameterName}`
        );

      case PREFERENCE:
        return getPreferenceBasicDetailsFromCache(
          `${contextId}::${controlParameterName}`
        );

      default: {
        return null;
      }
    }
  }

  #getControlParameterValueObject(controlParameterName, controlParameterType) {
    const contextIdsLength = _.size(this.#contextIds);

    /**
     * Q: Why contextIds are iterated in reverse order?
     * A: Order of contextIds represents the heirarchy of contexts in UI (Org>Program>Course>Class). So, the last one
     *    represents the current context and second last represents its ancestor and so on.
     */
    for (let i = contextIdsLength - 1; i >= 0; i--) {
      const contextId = this.#contextIds[i];
      const controlParameterValueObject = this.#getControlParameterValueObjectByContextId(
        controlParameterName,
        controlParameterType,
        contextId
      );

      if (!_.isNil(controlParameterValueObject)) {
        return controlParameterValueObject;
      }
    }

    return null;
  }

  getPermissionValue(permissionName) {
    let permissionValue = false;

    const controlParameterValueObject = this.#getControlParameterValueObject(
      permissionName,
      PERMISSION
    );

    if (!_.isNil(controlParameterValueObject)) {
      permissionValue = controlParameterValueObject.value;
    }

    const addOnFeatureFlag =
      _ADD_ON_CONTROL_PARAMETERS_MAP_OBJECT[permissionName];

    if (addOnFeatureFlag) {
      permissionValue &&= ACLStore.can(addOnFeatureFlag);
    }

    return permissionValue;
  }

  getFeatureFlagValue(featureFlagName) {
    const controlParameterValueObject = this.#getControlParameterValueObject(
      featureFlagName,
      FEATURE_FLAG
    );

    if (!_.isNil(controlParameterValueObject)) {
      return controlParameterValueObject.value;
    }

    return false;
  }

  getPreferenceValue(preferenceName) {
    const controlParameterValueObject = this.#getControlParameterValueObject(
      preferenceName,
      PREFERENCE
    );

    if (!_.isNil(controlParameterValueObject)) {
      return parsePreferenceValueObject(controlParameterValueObject);
    }

    return null;
  }

  getModuleSettingsValue(moduleName) {
    let moduleSettingsValue = false;

    const controlParameterValueObject = this.#getControlParameterValueObject(
      moduleName,
      MODULE_SETTINGS
    );

    if (!_.isNil(controlParameterValueObject)) {
      moduleSettingsValue = controlParameterValueObject.value;
    }

    const addOnFeatureFlag = _ADD_ON_CONTROL_PARAMETERS_MAP_OBJECT[moduleName];

    if (addOnFeatureFlag) {
      moduleSettingsValue &&= ACLStore.can(addOnFeatureFlag);
    }

    return moduleSettingsValue;
  }

  getPermissionValueByContext(permissionName, contexts) {
    const contextId = getContextIdForRolesAndPermissions(contexts);

    let permissionValue = false;

    permissionValue =
      this.#getControlParameterValueObjectByContextId(
        permissionName,
        PERMISSION,
        contextId
      )?.value ?? false;

    const addOnFeatureFlag =
      _ADD_ON_CONTROL_PARAMETERS_MAP_OBJECT[permissionName];

    if (addOnFeatureFlag) {
      permissionValue &&= this.#getConfigurationsValueForStaffByContext(
        addOnFeatureFlag,
        contexts
      );
    }

    return permissionValue;
  }

  getModuleSettingsValueByContext(moduleName, contexts, addOnContexts) {
    const contextId = getContextIdForRolesAndPermissions(contexts);

    let moduleSettingsValue = false;

    moduleSettingsValue =
      this.#getControlParameterValueObjectByContextId(
        moduleName,
        MODULE_SETTINGS,
        contextId
      )?.value ?? false;

    const addOnFeatureFlag = _ADD_ON_CONTROL_PARAMETERS_MAP_OBJECT[moduleName];

    if (addOnFeatureFlag) {
      /**
       * [TEMP] : We need to allow to pass separate contexts param for checking addOns feature flags.
       * This is required when addOns and RnP control parameter exist in different contexts.
       * For eg : In case of AttendanceSettingsV2, addOn feature flag exist at organization level
       * and RnP control parameter exist at curriculum program level.
       * We will remove this once Attendance settings v2 is enabled for all orgs.
       */

      const configurationContexts = _.isUndefined(addOnContexts)
        ? contexts
        : addOnContexts;

      moduleSettingsValue &&= this.#getConfigurationsValueForStaffByContext(
        addOnFeatureFlag,
        configurationContexts
      );
    }

    return moduleSettingsValue;
  }

  appendContextId(contextId) {
    const contextArrayLength = _.size(this.#contextIds);
    const isContextPresent =
      this.#contextIds[contextArrayLength - 1] === contextId;

    //If context is already present, then we don't need to append it again. Instead, we will increment the count.
    if (isContextPresent) {
      this.#countOfComponentsUsingParticularContext[
        contextArrayLength - 1
      ] += 1;

      return;
    }

    this.#contextIds.push(contextId);
    this.#countOfComponentsUsingParticularContext.push(1);
  }

  removeContextById(contextId) {
    const contextIdIndex = _.findLastIndex(
      this.#contextIds,
      id => id === contextId
    );

    if (contextIdIndex === -1) {
      return;
    }

    /**
     * If more than one component is using the same context, then we don't need to remove the context.
     * Instead, we will decrement the count.
     * Else we will remove the context and corresponding count both
     */
    if (this.#countOfComponentsUsingParticularContext[contextIdIndex] > 1) {
      this.#countOfComponentsUsingParticularContext[contextIdIndex] -= 1;

      return;
    }

    this.#contextIds = _.filter(
      this.#contextIds,
      (id, index) => index !== contextIdIndex
    );
    this.#countOfComponentsUsingParticularContext = _.filter(
      this.#countOfComponentsUsingParticularContext,
      (count, index) => index !== contextIdIndex
    );
  }

  updateContextsAndContextIdMap(contexts, contextId) {
    const stringifiedContexts = contexts
      .map(context => `${context.entityType}:${context.entityId}`)
      .join(",");
    this.#contextsAndContextIdMap[`${stringifiedContexts}`] = contextId;
  }

  getContextIdFromContexts(contexts) {
    const stringifiedContexts = contexts
      .map(context => `${context.entityType}:${context.entityId}`)
      .join(",");
    return this.#contextsAndContextIdMap[`${stringifiedContexts}`];
  }

  getControlParameterIdByContext(
    controlParameterName,
    controlParameterType = PERMISSION,
    contexts
  ) {
    const contextId = getContextIdForRolesAndPermissions(contexts);

    return (
      this.#getControlParameterValueObjectByContextId(
        controlParameterName,
        controlParameterType,
        contextId
      )?.id ?? ""
    );
  }
}

const aclStorage = new ACLStorage();
export default aclStorage;
export { ACLStorage };
